import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Line, Bar } from "react-chartjs-2";
import article from "./TaxifyContent";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
} from "chart.js";
import './PagesCss/StockProfitCalculator.css';

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
);

const StockCapitalGainsCalculator = () => {
  const content = article.find((item) => item.id === 1);
  const [activeTab, setActiveTab] = useState("calculator");
  const [isMobile, setIsMobile] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const [inputs, setInputs] = useState({
    assetName: "",
    purchasePrice: "50.00",
    sellingPrice: "75.00",
    quantity: "100",
    holdingPeriod: "400",
    brokerage: "10.00",
    stt: "2.00",
    deductibleExpenses: "5.00",
    carryforwardLosses: "0.00",
    dividendReinvestment: "0.00",
    currencyConversion: "1",
    country: "us",
    taxYear: "2024",
    incomeBracket: "middle",
    additionalTax: "0.00",
    inflationRate: "3",
    multiplePurchases: "1",
    washSale: "0"
  });

  const [result, setResult] = useState(null);
  const [calculationHistory, setCalculationHistory] = useState([]);
  const [taxScenarios, setTaxScenarios] = useState([]);

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "calculator") {
      calculateTax();
    }
  }, [inputs, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const getTaxRates = (country, holdingPeriod, incomeBracket) => {
    const days = parseFloat(holdingPeriod);
    const isLongTerm = days >= 365;
    
    const taxRates = {
      us: {
        shortTerm: {
          low: 0.10,
          middle: 0.22,
          high: 0.35,
          veryHigh: 0.37
        },
        longTerm: {
          low: 0.00,
          middle: 0.15,
          high: 0.20,
          veryHigh: 0.23
        }
      },
      uk: {
        shortTerm: {
          low: 0.10,
          middle: 0.20,
          high: 0.40,
          veryHigh: 0.45
        },
        longTerm: {
          low: 0.10,
          middle: 0.20,
          high: 0.40,
          veryHigh: 0.45
        }
      },
      india: {
        shortTerm: {
          low: 0.15,
          middle: 0.15,
          high: 0.15,
          veryHigh: 0.15
        },
        longTerm: {
          low: 0.10,
          middle: 0.10,
          high: 0.10,
          veryHigh: 0.10
        }
      },
      canada: {
        shortTerm: {
          low: 0.15,
          middle: 0.205,
          high: 0.26,
          veryHigh: 0.29
        },
        longTerm: {
          low: 0.075,
          middle: 0.1025,
          high: 0.13,
          veryHigh: 0.145
        }
      }
    };

    const rates = taxRates[country] || taxRates.us;
    return isLongTerm ? rates.longTerm[incomeBracket] : rates.shortTerm[incomeBracket];
  };

  const calculateTax = () => {
    const {
      assetName,
      purchasePrice,
      sellingPrice,
      quantity,
      holdingPeriod,
      brokerage,
      stt,
      deductibleExpenses,
      carryforwardLosses,
      dividendReinvestment,
      currencyConversion,
      country,
      taxYear,
      incomeBracket,
      additionalTax,
      inflationRate,
      multiplePurchases,
      washSale
    } = inputs;

    if (!purchasePrice || !sellingPrice || !quantity || !holdingPeriod) {
      return;
    }

    const buy = parseFloat(purchasePrice);
    const sell = parseFloat(sellingPrice);
    const qty = parseFloat(quantity);
    const holdDays = parseFloat(holdingPeriod);
    const broker = parseFloat(brokerage || 0);
    const taxStt = parseFloat(stt || 0);
    const dedExp = parseFloat(deductibleExpenses || 0);
    const carryLoss = parseFloat(carryforwardLosses || 0);
    const divReinvest = parseFloat(dividendReinvestment || 0);
    const curr = parseFloat(currencyConversion || 1);
    const addTax = parseFloat(additionalTax || 0);
    const inflation = parseFloat(inflationRate || 0) / 100;
    const multiPurchase = parseFloat(multiplePurchases || 1);
    const washSaleAmount = parseFloat(washSale || 0);

    // Calculate cost basis with adjustments
    const baseCost = (buy * qty * multiPurchase) + broker + dedExp - divReinvest;
    const adjustedCost = baseCost * (1 + inflation);
    
    // Calculate proceeds
    const totalProceeds = (sell * qty * multiPurchase) - broker - taxStt - washSaleAmount;
    
    // Calculate gain/loss
    let totalGain = totalProceeds - adjustedCost - carryLoss;

    // Determine tax treatment
    const isShortTerm = holdDays < 365;
    const taxRate = getTaxRates(country, holdDays, incomeBracket);
    const taxAmount = totalGain > 0 ? (totalGain * taxRate) + addTax : 0;

    // Net calculations
    const netProceeds = totalProceeds - taxAmount;
    const effectiveTaxRate = totalGain > 0 ? (taxAmount / totalGain) * 100 : 0;

    // Currency conversion
    const convertedGain = totalGain * curr;
    const convertedTax = taxAmount * curr;
    const convertedNet = netProceeds * curr;

    // Holding period classification
    let holdingPeriodType = "Short-Term";
    if (holdDays >= 365 && holdDays < 1825) holdingPeriodType = "Long-Term";
    if (holdDays >= 1825) holdingPeriodType = "Very Long-Term";

    // Tax efficiency metrics
    const taxEfficiency = ((totalGain - taxAmount) / totalGain) * 100;
    const annualizedReturn = ((sell / buy) ** (365 / holdDays) - 1) * 100;
    const afterTaxReturn = annualizedReturn * (1 - (taxRate || 0));

    // Generate tax scenarios
    const scenarios = generateTaxScenarios(totalGain, holdDays, country, incomeBracket);

    setResult({
      assetName: assetName || "Your Asset",
      totalGain: convertedGain,
      taxAmount: convertedTax,
      netProceeds: convertedNet,
      gainType: isShortTerm ? "Short-Term Capital Gain" : "Long-Term Capital Gain",
      effectiveTaxRate: effectiveTaxRate,
      taxRate: taxRate * 100,
      holdingPeriodType: holdingPeriodType,
      costBasis: adjustedCost * curr,
      totalProceeds: totalProceeds * curr,
      taxEfficiency: taxEfficiency,
      annualizedReturn: annualizedReturn,
      afterTaxReturn: afterTaxReturn,
      holdingDays: holdDays,
      isTaxable: totalGain > 0
    });

    setTaxScenarios(scenarios);
  };

  const generateTaxScenarios = (totalGain, holdDays, country, incomeBracket) => {
    const scenarios = [];
    
    // Current scenario
    scenarios.push({
      name: "Current",
      holdingPeriod: holdDays,
      taxRate: getTaxRates(country, holdDays, incomeBracket) * 100,
      taxAmount: totalGain * getTaxRates(country, holdDays, incomeBracket),
      netAmount: totalGain - (totalGain * getTaxRates(country, holdDays, incomeBracket))
    });

    // Alternative holding periods
    const periods = [30, 180, 364, 365, 730, 1825];
    periods.forEach(days => {
      if (days !== holdDays) {
        const rate = getTaxRates(country, days, incomeBracket);
        scenarios.push({
          name: `${days} days`,
          holdingPeriod: days,
          taxRate: rate * 100,
          taxAmount: totalGain * rate,
          netAmount: totalGain - (totalGain * rate)
        });
      }
    });

    // Alternative income brackets
    const brackets = ['low', 'middle', 'high', 'veryHigh'];
    brackets.forEach(bracket => {
      if (bracket !== incomeBracket) {
        const rate = getTaxRates(country, holdDays, bracket);
        scenarios.push({
          name: `${bracket} bracket`,
          holdingPeriod: holdDays,
          taxRate: rate * 100,
          taxAmount: totalGain * rate,
          netAmount: totalGain - (totalGain * rate)
        });
      }
    });

    return scenarios.sort((a, b) => a.taxRate - b.taxRate);
  };

  const calculateTaxOptimization = () => {
    const { purchasePrice, sellingPrice, quantity, holdingPeriod, country } = inputs;
    
    if (!purchasePrice || !sellingPrice || !quantity) return null;

    const buy = parseFloat(purchasePrice);
    const sell = parseFloat(sellingPrice);
    const qty = parseFloat(quantity);
    const gain = (sell - buy) * qty;

    const optimizationStrategies = [];

    // Tax-loss harvesting
    if (gain > 0) {
      optimizationStrategies.push({
        strategy: "Tax-Loss Harvesting",
        description: "Offset gains with losses from other investments",
        potentialSavings: gain * 0.15,
        complexity: "Medium"
      });
    }

    // Holding period optimization
    const currentDays = parseFloat(holdingPeriod);
    if (currentDays < 365) {
      optimizationStrategies.push({
        strategy: "Hold for Long-Term",
        description: "Wait until holding period reaches 365 days for lower tax rates",
        potentialSavings: gain * (getTaxRates(country, currentDays, 'middle') - getTaxRates(country, 365, 'middle')),
        complexity: "Low"
      });
    }

    // Income timing
    optimizationStrategies.push({
      strategy: "Income Timing",
      description: "Realize gains in years with lower income",
      potentialSavings: gain * 0.05,
      complexity: "High"
    });

    return optimizationStrategies;
  };

  const resetCalculator = () => {
    setInputs({
      assetName: "",
      purchasePrice: "50.00",
      sellingPrice: "75.00",
      quantity: "100",
      holdingPeriod: "400",
      brokerage: "10.00",
      stt: "2.00",
      deductibleExpenses: "5.00",
      carryforwardLosses: "0.00",
      dividendReinvestment: "0.00",
      currencyConversion: "1",
      country: "us",
      taxYear: "2024",
      incomeBracket: "middle",
      additionalTax: "0.00",
      inflationRate: "3",
      multiplePurchases: "1",
      washSale: "0"
    });
    addNotification("Calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!result) return;
    
    const csvContent = [
      "Metric,Value",
      `Asset Name,${result.assetName}`,
      `Total Gain,${result.totalGain.toFixed(2)}`,
      `Capital Gains Tax,${result.taxAmount.toFixed(2)}`,
      `Net Proceeds,${result.netProceeds.toFixed(2)}`,
      `Gain Type,${result.gainType}`,
      `Effective Tax Rate,${result.effectiveTaxRate.toFixed(2)}%`,
      `Tax Rate,${result.taxRate.toFixed(2)}%`,
      `Holding Period,${result.holdingDays} days`,
      `Annualized Return,${result.annualizedReturn.toFixed(2)}%`,
      `After-Tax Return,${result.afterTaxReturn.toFixed(2)}%`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'capital-gains-calculation.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Data exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!result) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      result: { ...result }
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Calculation saved to history", "success");
  };

  const loadCalculation = (calc) => {
    setInputs(calc.inputs);
    setResult(calc.result);
    setShowSaved(false);
    addNotification("Calculation loaded successfully", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      shortTerm: { 
        holdingPeriod: "100",
        country: "us",
        incomeBracket: "middle"
      },
      longTerm: { 
        holdingPeriod: "500",
        country: "us", 
        incomeBracket: "middle"
      },
      taxFree: {
        holdingPeriod: "500",
        country: "us",
        incomeBracket: "low"
      }
    };
    
    setInputs(prev => ({ ...prev, ...presets[preset] }));
    addNotification(`${preset} tax preset applied`, "info");
  };

  // Chart Data
  const taxChartData = result ? {
    labels: ["Net Proceeds", "Capital Gains Tax", "Cost Basis"],
    datasets: [{
      data: [
        Math.max(result.netProceeds, 0),
        Math.max(result.taxAmount, 0),
        Math.max(result.costBasis, 0)
      ],
      backgroundColor: ["#28A745", "#FF6B6B", "#4ECDC4"],
      borderWidth: 2,
      hoverOffset: 15
    }],
  } : null;

  const scenarioChartData = taxScenarios.length > 0 ? {
    labels: taxScenarios.map(s => s.name),
    datasets: [{
      label: 'Tax Rate',
      data: taxScenarios.map(s => s.taxRate),
      backgroundColor: taxScenarios.map(s => 
        s.taxRate < 15 ? '#28A745' : 
        s.taxRate < 25 ? '#FFD93D' : '#FF6B6B'
      ),
      borderWidth: 2
    }]
  } : null;

  const optimizationStrategies = calculateTaxOptimization();

  return (
    <div className="profit-calculator">
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Capital Gains Tax Calculator</h1>
        </div>
        <p>Advanced tax calculations with multi-country support and optimization strategies</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      {/* Saved Calculations Modal */}
      {showSaved && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Saved Calculations</h3>
              <button className="close-btn" onClick={() => setShowSaved(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="saved-list">
              {savedCalculations.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-bookmark fa-2x"></i>
                  <p>No saved calculations yet</p>
                </div>
              ) : (
                savedCalculations.map(calc => (
                  <div key={calc.id} className="saved-item" onClick={() => loadCalculation(calc)}>
                    <div className="saved-name">{calc.result.assetName}</div>
                    <div className="saved-details">
                      <span className={`profit ${calc.result.totalGain >= 0 ? 'positive' : 'negative'}`}>
                        ${calc.result.totalGain.toFixed(2)}
                      </span>
                      <span className="date">{calc.timestamp}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "calculator" ? "active" : ""}`}
                onClick={() => setActiveTab("calculator")}
              >
                <i className="fas fa-calculator"></i>
                {isMobile ? 'Calculator' : 'Tax Calculator'}
              </button>
              <button 
                className={`tab ${activeTab === "scenarios" ? "active" : ""}`}
                onClick={() => setActiveTab("scenarios")}
              >
                <i className="fas fa-chart-bar"></i>
                {isMobile ? 'Scenarios' : 'Tax Scenarios'}
              </button>
              <button 
                className={`tab ${activeTab === "optimization" ? "active" : ""}`}
                onClick={() => setActiveTab("optimization")}
              >
                <i className="fas fa-lightbulb"></i>
                {isMobile ? 'Optimize' : 'Tax Optimization'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Quick Presets */}
          <div className="presets-section">
            <h4>Tax Presets:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('shortTerm')}>
                Short-Term
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('longTerm')}>
                Long-Term
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('taxFree')}>
                Tax-Free
              </button>
            </div>
          </div>

          {/* Calculator Tab */}
          {activeTab === "calculator" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  <h3>
                    <i className="fas fa-chart-line"></i>
                    Investment Details
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-signature"></i> Asset Name</label>
                      <input 
                        type="text" 
                        name="assetName" 
                        value={inputs.assetName} 
                        onChange={handleChange}
                        placeholder="Stock / ETF / Mutual Fund Name"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Purchase Price</label>
                      <input 
                        type="number" 
                        name="purchasePrice" 
                        value={inputs.purchasePrice} 
                        onChange={handleChange}
                        placeholder="50.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Selling Price</label>
                      <input 
                        type="number" 
                        name="sellingPrice" 
                        value={inputs.sellingPrice} 
                        onChange={handleChange}
                        placeholder="75.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-layer-group"></i> Quantity</label>
                      <input 
                        type="number" 
                        name="quantity" 
                        value={inputs.quantity} 
                        onChange={handleChange}
                        placeholder="100"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-calendar"></i> Holding Period (Days)</label>
                      <input 
                        type="number" 
                        name="holdingPeriod" 
                        value={inputs.holdingPeriod} 
                        onChange={handleChange}
                        placeholder="400"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-flag"></i> Country</label>
                      <select name="country" value={inputs.country} onChange={handleChange}>
                        <option value="us">United States</option>
                        <option value="uk">United Kingdom</option>
                        <option value="india">India</option>
                        <option value="canada">Canada</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-sliders-h"></i>
                    Cost & Tax Settings
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-coins"></i> Brokerage Fees</label>
                      <input 
                        type="number" 
                        name="brokerage" 
                        value={inputs.brokerage} 
                        onChange={handleChange}
                        placeholder="10.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-file-invoice-dollar"></i> STT/Charges</label>
                      <input 
                        type="number" 
                        name="stt" 
                        value={inputs.stt} 
                        onChange={handleChange}
                        placeholder="2.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-money-bill-wave"></i> Income Bracket</label>
                      <select name="incomeBracket" value={inputs.incomeBracket} onChange={handleChange}>
                        <option value="low">Low (10-15%)</option>
                        <option value="middle">Middle (22-25%)</option>
                        <option value="high">High (35-40%)</option>
                        <option value="veryHigh">Very High (45%+)</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-receipt"></i> Deductible Expenses</label>
                      <input 
                        type="number" 
                        name="deductibleExpenses" 
                        value={inputs.deductibleExpenses} 
                        onChange={handleChange}
                        placeholder="5.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrow-down"></i> Carryforward Losses</label>
                      <input 
                        type="number" 
                        name="carryforwardLosses" 
                        value={inputs.carryforwardLosses} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrows-repeat"></i> Currency Rate</label>
                      <input 
                        type="number" 
                        name="currencyConversion" 
                        value={inputs.currencyConversion} 
                        onChange={handleChange}
                        placeholder="1.00"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save to History
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Tax Scenarios Tab */}
          {activeTab === "scenarios" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-cogs"></i>
                  Advanced Tax Settings
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-calendar-alt"></i> Tax Year</label>
                    <input 
                      type="number" 
                      name="taxYear" 
                      value={inputs.taxYear} 
                      onChange={handleChange}
                      placeholder="2024"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-percentage"></i> Additional Tax</label>
                    <input 
                      type="number" 
                      name="additionalTax" 
                      value={inputs.additionalTax} 
                      onChange={handleChange}
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-chart-line"></i> Inflation Rate (%)</label>
                    <input 
                      type="number" 
                      name="inflationRate" 
                      value={inputs.inflationRate} 
                      onChange={handleChange}
                      placeholder="3"
                      step="0.1"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-copy"></i> Multiple Purchases</label>
                    <input 
                      type="number" 
                      name="multiplePurchases" 
                      value={inputs.multiplePurchases} 
                      onChange={handleChange}
                      placeholder="1"
                      min="1"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-recycle"></i> Dividend Reinvestment</label>
                    <input 
                      type="number" 
                      name="dividendReinvestment" 
                      value={inputs.dividendReinvestment} 
                      onChange={handleChange}
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-ban"></i> Wash Sale Amount</label>
                    <input 
                      type="number" 
                      name="washSale" 
                      value={inputs.washSale} 
                      onChange={handleChange}
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>
                </div>

                {taxScenarios.length > 0 && (
                  <div className="position-breakdown">
                    <h4>Scenario Comparison</h4>
                    <div className="scenario-list">
                      {taxScenarios.slice(0, 3).map((scenario, index) => (
                        <div key={index} className="scenario-item">
                          <div className="breakdown-item">
                            <span className="scenario-name">{scenario.name}</span>
                            <span className="scenario-outcome">
                              {scenario.taxRate.toFixed(1)}%
                            </span>
                          </div>
                          <div className="breakdown-item">
                            <span>Tax: ${scenario.taxAmount.toFixed(2)}</span>
                            <span>Net: ${scenario.netAmount.toFixed(2)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tax Optimization Tab */}
          {activeTab === "optimization" && optimizationStrategies && (
            <div className="risk-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-lightbulb"></i>
                  Tax Optimization Strategies
                </h3>
                
                <div className="position-breakdown">
                  {optimizationStrategies.map((strategy, index) => (
                    <div key={index} className="">
                      <div className="breakdown-item">
                        <span className="optimization-name">{strategy.strategy}</span>
                        <span className="optimization-savings positive">
                          Save ${strategy.potentialSavings.toFixed(2)}
                        </span>
                      </div>
                      <div className="breakdown-item">
                        <span>{strategy.description}</span>
                        <span className={`complexity ${strategy.complexity.toLowerCase()}`}>
                          {strategy.complexity} Complexity
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Recent Calculations
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No calculations yet. Start calculating to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className={`history-profit ${calc.result.totalGain >= 0 ? 'positive' : 'negative'}`}>
                          <i className={`fas ${calc.result.totalGain >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'}`}></i>
                          ${calc.result.totalGain.toFixed(2)}
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-percentage"></i> Tax: {calc.result.effectiveTaxRate.toFixed(2)}%</span>
                        <span><i className="fas fa-calendar"></i> {calc.result.holdingDays} days</span>
                        <span><i className="fas fa-chart-line"></i> {calc.result.annualizedReturn.toFixed(2)}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "calculator" || activeTab === "scenarios" || activeTab === "optimization") && (
          <div className="right-panel">
            {activeTab === "calculator" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-file-invoice-dollar"></i>
                    Tax Analysis
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {result ? (
                  <>
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className={`result-value ${result.totalGain >= 0 ? 'positive' : 'negative'}`}>
                          ${result.totalGain.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-chart-line"></i>
                          Total Capital Gain
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value negative">
                          ${result.taxAmount.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-file-invoice-dollar"></i>
                          Capital Gains Tax
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value positive">
                          ${result.netProceeds.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-hand-holding-usd"></i>
                          Net Proceeds
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.effectiveTaxRate.toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-percentage"></i>
                          Effective Tax Rate
                        </div>
                      </div>
                    </div>

                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-signature"></i> Asset Name:</span>
                        <span>{result.assetName}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-balance-scale"></i> Gain Type:</span>
                        <span>{result.gainType}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-percentage"></i> Tax Rate:</span>
                        <span>{result.taxRate.toFixed(2)}%</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-calendar"></i> Holding Period:</span>
                        <span>{result.holdingDays} days ({result.holdingPeriodType})</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-chart-line"></i> Annualized Return:</span>
                        <span>{result.annualizedReturn.toFixed(2)}%</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-chart-line"></i> After-Tax Return:</span>
                        <span>{result.afterTaxReturn.toFixed(2)}%</span>
                      </div>
                    </div>

                    {/* Tax Efficiency */}
                    <div className="risk-summary">
                      <h4>Tax Efficiency</h4>
                      <div className="risk-cards">
                        <div className="risk-card">
                          <div className={`risk-value ${result.taxEfficiency > 80 ? 'positive' : result.taxEfficiency > 60 ? 'moderate' : 'negative'}`}>
                            {result.taxEfficiency.toFixed(1)}%
                          </div>
                          <div className="risk-label">Tax Efficiency</div>
                        </div>
                        <div className="risk-card">
                          <div className={`risk-value ${result.effectiveTaxRate < 15 ? 'positive' : result.effectiveTaxRate < 25 ? 'moderate' : 'negative'}`}>
                            {result.effectiveTaxRate < 15 ? 'Low' : result.effectiveTaxRate < 25 ? 'Medium' : 'High'}
                          </div>
                          <div className="risk-label">Tax Burden</div>
                        </div>
                      </div>
                    </div>

                    {/* Chart - FIXED TOOLTIP */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-pie"></i>
                        Proceeds Distribution
                      </h3>
                      <Doughnut 
                        data={taxChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'bottom'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  const value = context.parsed;
                                  if (typeof value === 'number' && !isNaN(value)) {
                                    return `$${value.toFixed(2)}`;
                                  }
                                  return `$${0}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-file-invoice-dollar fa-3x"></i>
                    <p>Enter your investment details to calculate capital gains tax</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "scenarios" && taxScenarios.length > 0 && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-chart-bar"></i>
                  Tax Scenario Analysis
                </h3>
                
                <div className="chart-container">
                  <Bar 
                    data={scenarioChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          display: false
                        },
                        tooltip: {
                          callbacks: {
                            label: function(context) {
                              const value = context.parsed;
                              if (typeof value === 'number' && !isNaN(value)) {
                                return `${value.toFixed(1)}%`;
                              }
                              return `${0}%`;
                            }
                          }
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Tax Rate (%)'
                          }
                        }
                      }
                    }}
                  />
                </div><br/>

                <div className="position-breakdown">
                  <h4>Best Scenarios</h4>
                  <div className="scenario-stats">
                    <div className="breakdown-item">
                      <span>Lowest Tax Rate:</span>
                      <span className="positive">{taxScenarios[0].taxRate.toFixed(1)}%</span>
                    </div>
                    <div className="breakdown-item">
                      <span>Highest Tax Rate:</span>
                      <span className="negative">{taxScenarios[taxScenarios.length - 1].taxRate.toFixed(1)}%</span>
                    </div>
                    <div className="breakdown-item">
                      <span>Potential Savings:</span>
                      <span className="positive">
                        ${(taxScenarios[taxScenarios.length - 1].taxAmount - taxScenarios[0].taxAmount).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "optimization" && optimizationStrategies && (
              <div className="risk-results-panel">
                <h3>
                  <i className="fas fa-lightbulb"></i>
                  Tax Optimization Summary
                </h3><br/>
                
                <div className="optimization-summary">
                  <div className="summary-card">
                    <div className="summary-value positive">
                      ${optimizationStrategies.reduce((sum, strategy) => sum + strategy.potentialSavings, 0).toFixed(2)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-dollar-sign"></i>
                      Total Potential Savings
                    </div>
                  </div><br/>
                  <div className="summary-card">
                    <div className="summary-value">
                      {optimizationStrategies.length}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-lightbulb"></i>
                      Available Strategies
                    </div>
                  </div><br/>
                  <div className="summary-card">
                    <div className="summary-value">
                      {optimizationStrategies.filter(s => s.complexity === 'Low').length}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-bolt"></i>
                      Easy Strategies
                    </div>
                  </div>
                </div><br/>

                <div className="position-breakdown">
                  <h4>Implementation Priority</h4>
                  {optimizationStrategies
                    .sort((a, b) => a.complexity.localeCompare(b.complexity))
                    .map((strategy, index) => (
                      <div key={index} className="breakdown-item">
                        <span>{strategy.strategy}:</span>
                        <span className={`priority ${strategy.complexity.toLowerCase()}`}>
                          {strategy.complexity} Priority
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default StockCapitalGainsCalculator;